import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminApi, type Company } from "../../api/admin";
import type { ApiError } from "../../api/client";
import {
  reportsApi,
  type Dispute,
  type DisputeRequest,
  type RiskNote,
  type RiskNoteRequest,
} from "../../api/reports";
import { CompanyCombobox } from "../../components/CompanyCombobox";
import { useConfirm } from "../../components/ConfirmDialog";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";
import { TableSkeleton } from "../../components/Loading";
import { Pager } from "../../components/Pager";
import { formatDate, formatNumber } from "../../lib/format";

/**
 * Dve stvari koje ERP ne drži, pa ih neko unosi rukom.
 *
 * Štampani izveštaj piše „sporno oko 130.000.000" i analizu rizičnih kupaca u slobodnom tekstu.
 * Dok to nije zapis, „oko" se ne može zameniti brojem i niko ne zna ko je procenu napravio. Zato
 * oba registra: sporni iznos po firmi i valuti, i beleška sa datumom.
 *
 * Oznaka visi na FIRMI a ne na računu, jer račun nema identitet — uvoz briše sve ERP-ove račune i
 * upiše ih iznova, pa bi kolona na tom redu nestajala pri svakom uvozu.
 *
 * Spisak se čita po stranama, i prazno pri prvom otvaranju nije isto što i prazno pri prelasku na
 * stranu: prvo je kostur, drugo zadržava zatečene redove.
 */


const VALUTE = ["RSD", "EUR", "USD"] as const;

/** Mora da se poklapa sa `STRANA` u `ReceivablesReportController`: Pager iz nje računa strane. */
const STRANA = 25;

export default function SpornoIBeleske({ locale, canWrite }: { locale: string; canWrite: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SpornoRegistar locale={locale} canWrite={canWrite} />
      <BeleskeRegistar locale={locale} canWrite={canWrite} />
    </div>
  );
}

/** Firme se traže na serveru; spisak od 200 koji se seče u pregledaču krije firmu koja postoji. */
function useFirme() {
  const [firme, setFirme] = useState<Company[]>([]);
  const [ukupno, setUkupno] = useState(0);
  const [upit, setUpit] = useState("");
  useEffect(() => {
    let otkazano = false;
    void adminApi
      .listCompanies(0, 25, undefined, upit)
      .then((p) => {
        if (otkazano) return;
        setFirme(p.content);
        setUkupno(p.totalElements);
      })
      .catch(() => undefined);
    return () => {
      otkazano = true;
    };
  }, [upit]);
  return { firme, ukupno, setUpit };
}

function SpornoRegistar({ locale, canWrite }: { locale: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const { firme, ukupno: ukupnoFirmi, setUpit } = useFirme();

  const [strana, setStrana] = useState(0);
  const [redovi, setRedovi] = useState<Dispute[] | null>(null);
  const [ukupno, setUkupno] = useState(0);
  const [ucitava, setUcitava] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);

  const [uredjuje, setUredjuje] = useState<Dispute | null>(null);
  const [novi, setNovi] = useState(false);

  const ucitaj = useCallback(async () => {
    setUcitava(true);
    try {
      const p = await reportsApi.disputes(strana);
      setRedovi(p.content);
      setUkupno(p.totalElements);
      setGreska(null);
    } catch (e) {
      setGreska((e as ApiError).message);
    } finally {
      setUcitava(false);
    }
  }, [strana]);

  useEffect(() => {
    void ucitaj();
  }, [ucitaj]);

  const zbir = useMemo(
    () =>
      (redovi ?? [])
        .filter((d) => d.resolvedOn == null)
        .reduce<Record<string, number>>((acc, d) => {
          acc[d.currency] = (acc[d.currency] ?? 0) + d.amount;
          return acc;
        }, {}),
    [redovi],
  );

  async function sacuvaj(body: DisputeRequest, id?: string) {
    if (id) await reportsApi.updateDispute(id, body);
    else await reportsApi.createDispute(body);
    setNovi(false);
    setUredjuje(null);
    await ucitaj();
    toast.success(t("report.recv.savedDispute", "Sporno potraživanje je upisano."));
  }

  /**
   * Brisanje traži potvrdu, kao svuda drugde u portalu.
   *
   * Oznaka nosi iznos koji ulazi u zbir koji menadžment čita; jedan promašen klik i zbir se
   * promeni, a niko ne vidi da je nešto nestalo. Brisanje je meko i dnevnik ga pamti, ali čovek
   * koji je kliknuo to ne zna dok ne pita.
   */
  async function obrisi(d: Dispute) {
    const potvrda = await confirm({
      message: t("report.recv.confirmDeleteDispute", {
        kupac: d.companyName ?? "—",
        iznos: formatNumber(d.amount, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        valuta: d.currency,
        defaultValue: "Obrisati spornu oznaku za {{kupac}} na {{iznos}} {{valuta}}?",
      }),
      danger: true,
    });
    if (!potvrda) return;
    try {
      await reportsApi.deleteDispute(d.id);
      await ucitaj();
      toast.success(t("report.recv.deletedDispute", "Oznaka je obrisana."));
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  return (
    <section className="card" style={{ padding: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 14px 0",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("report.recv.disputes", "Sporna potraživanja")}</h3>
          <div className="sub">
            {t(
              "report.recv.disputesSub",
              "Naša oznaka — ERP je ne drži. Zato se „oko“ iz izveštaja može zameniti brojem koji se sabira.",
            )}
          </div>
        </div>
        {canWrite && !novi && !uredjuje && (
          <button type="button" className="btn" onClick={() => setNovi(true)}>
            {t("report.recv.addDispute", "Dodaj oznaku")}
          </button>
        )}
      </div>

      {Object.keys(zbir).length > 0 && (
        <div className="sub" style={{ padding: "8px 14px 0" }}>
          {t("report.recv.openTotal", "Otvoreno na ovoj strani:")}{" "}
          {Object.entries(zbir)
            .map(([v, i]) => `${formatNumber(i, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${v}`)
            .join(" · ")}
        </div>
      )}

      {greska && <div className="card error" style={{ margin: 14 }}>{greska}</div>}

      {(novi || uredjuje) && (
        <SpornoObrazac
          postojeci={uredjuje}
          firme={firme}
          ukupnoFirmi={ukupnoFirmi}
          onTraziFirmu={setUpit}
          onOdustani={() => {
            setNovi(false);
            setUredjuje(null);
          }}
          onSacuvaj={sacuvaj}
        />
      )}

      {redovi === null && ucitava && <TableSkeleton rows={4} />}

      {/* PRAZNA EVIDENCIJA NIJE POTVRDA DA JE SPORNO NULA. Oznaku upisuje čovek, pa dok je nema
          portal ne zna ništa o spornom — a mesečni izveštaj menadžmentu godinama nosi red
          „Sporno oko 130.000.000 rsd". Prazan spisak koji ćuti o tome čita se kao nula. */}
      {redovi !== null && redovi.length === 0 && (
        <div className="empty" style={{ padding: 14 }}>
          {t("report.recv.noDisputes", "Nema upisanih spornih potraživanja.")}
          <div className="sub" style={{ marginTop: 6 }}>
            {t(
              "report.recv.noDisputesIncomplete",
              "Evidencija nije potpuna: oznaku upisuje komercijala, pa prazan spisak ne znači da spornog potraživanja nema.",
            )}
          </div>
        </div>
      )}

      {redovi !== null && redovi.length > 0 && (
        <div style={{ overflowX: "auto", opacity: ucitava ? 0.55 : 1 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("report.recv.col.customer", "Kupac")}</th>
                <th style={{ textAlign: "right" }}>{t("report.recv.col.amount", "Iznos")}</th>
                <th>{t("report.recv.col.reason", "Razlog")}</th>
                <th>{t("report.recv.col.openedOn", "Otvoreno")}</th>
                <th>{t("report.recv.col.resolvedOn", "Rešeno")}</th>
                <th>{t("report.recv.col.author", "Upisao")}</th>
                {canWrite && <th aria-label={t("common.actions", "Radnje")} />}
              </tr>
            </thead>
            <tbody>
              {redovi.map((d) => (
                <tr key={d.id}>
                  <td>{d.companyName ?? "—"}</td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {formatNumber(d.amount, locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {d.currency}
                  </td>
                  <td style={{ maxWidth: 340 }}>
                    {d.invoiceNumber && (
                      <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                        {t("disputes.onInvoice", "Invoice")} {d.invoiceNumber}
                      </div>
                    )}
                    {d.reason}
                  </td>
                  <td>{formatDate(d.openedOn, locale)}</td>
                  <td>
                    {d.resolvedOn ? (
                      formatDate(d.resolvedOn, locale)
                    ) : (
                      <span style={{ color: "var(--warn)" }}>{t("report.recv.open", "otvoreno")}</span>
                    )}
                  </td>
                  <td>{d.createdByName ?? "—"}</td>
                  {canWrite && (
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button type="button" className="btn ghost" onClick={() => setUredjuje(d)}>
                        {t("common.edit", "Uredi")}
                      </button>{" "}
                      <button type="button" className="btn ghost" onClick={() => void obrisi(d)}>
                        {t("common.delete", "Obriši")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager page={strana} size={STRANA} total={ukupno} busy={ucitava} onPage={setStrana} />
    </section>
  );
}

function SpornoObrazac({
  postojeci,
  firme,
  ukupnoFirmi,
  onTraziFirmu,
  onOdustani,
  onSacuvaj,
}: {
  postojeci: Dispute | null;
  firme: Company[];
  ukupnoFirmi: number;
  onTraziFirmu: (q: string) => void;
  onOdustani: () => void;
  onSacuvaj: (body: DisputeRequest, id?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const danas = new Date().toISOString().slice(0, 10);
  const [companyId, setCompanyId] = useState<string | null>(postojeci?.companyId ?? null);
  const [currency, setCurrency] = useState(postojeci?.currency ?? "RSD");
  const [amount, setAmount] = useState(postojeci ? String(postojeci.amount) : "");
  const [reason, setReason] = useState(postojeci?.reason ?? "");
  const [openedOn, setOpenedOn] = useState(postojeci?.openedOn ?? danas);
  const [resolvedOn, setResolvedOn] = useState(postojeci?.resolvedOn ?? "");
  const [resolution, setResolution] = useState(postojeci?.resolution ?? "");
  const [greska, setGreska] = useState<string | null>(null);
  const [cuva, setCuva] = useState(false);

  async function posalji(e: React.FormEvent) {
    e.preventDefault();
    if (!companyId) {
      setGreska(t("report.recv.err.company", "Izaberi kupca."));
      return;
    }
    const iznos = Number(amount.replace(",", "."));
    if (!Number.isFinite(iznos) || iznos <= 0) {
      setGreska(t("report.recv.err.amount", "Iznos mora biti veći od nule."));
      return;
    }
    // Rešenje i njegov datum putuju zajedno: rešeno bez objašnjenja i objašnjenje bez datuma su
    // svako pola zapisa, pa ih i server odbija — ovde se kaže pre nego što se pošalje.
    if (!!resolvedOn !== !!resolution.trim()) {
      setGreska(t("report.recv.err.resolutionPair", "Rešenje traži i datum i objašnjenje."));
      return;
    }
    setCuva(true);
    try {
      await onSacuvaj(
        {
          companyId,
          currency,
          amount: iznos,
          reason: reason.trim(),
          openedOn,
          resolvedOn: resolvedOn || null,
          resolution: resolution.trim() || null,
        },
        postojeci?.id,
      );
    } catch (err) {
      setGreska((err as ApiError).message);
    } finally {
      setCuva(false);
    }
  }

  return (
    <form
      onSubmit={posalji}
      style={{
        margin: 14,
        padding: 14,
        border: "1px solid var(--border)",
        borderRadius: 8,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      }}
    >
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>{t("report.recv.col.customer", "Kupac")} *</span>
        <CompanyCombobox
          companies={firme}
          value={companyId}
          onChange={setCompanyId}
          onQueryChange={onTraziFirmu}
          totalMatches={ukupnoFirmi}
          placeholder={t("report.recv.pickCustomer", "Traži kupca…")}
        />
      </label>
      <label className="field">
        <span>{t("report.recv.col.amount", "Iznos")} *</span>
        <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
      </label>
      <div className="field">
        <span>{t("report.recv.col.currency", "Valuta")} *</span>
        <Select
          value={currency}
          options={VALUTE.map((v) => ({ value: v, label: v }))}
          onChange={setCurrency}
          ariaLabel={t("report.recv.col.currency", "Valuta")}
        />
      </div>
      <label className="field">
        <span>{t("report.recv.col.openedOn", "Otvoreno")} *</span>
        <input className="input" type="date" value={openedOn} onChange={(e) => setOpenedOn(e.target.value)} />
      </label>
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>{t("report.recv.col.reason", "Razlog")} *</span>
        <textarea
          className="input"
          rows={2}
          maxLength={4000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <label className="field">
        <span>{t("report.recv.col.resolvedOn", "Rešeno")}</span>
        <input className="input" type="date" value={resolvedOn} onChange={(e) => setResolvedOn(e.target.value)} />
      </label>
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>{t("report.recv.col.resolution", "Objašnjenje rešenja")}</span>
        <textarea
          className="input"
          rows={2}
          maxLength={4000}
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
        />
      </label>
      {greska && (
        <div className="card error" style={{ gridColumn: "1 / -1" }}>
          {greska}
        </div>
      )}
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
        <button type="submit" className="btn" disabled={cuva}>
          {t("common.save", "Sačuvaj")}
        </button>
        <button type="button" className="btn ghost" onClick={onOdustani} disabled={cuva}>
          {t("common.cancel", "Odustani")}
        </button>
      </div>
    </form>
  );
}

function BeleskeRegistar({ locale, canWrite }: { locale: string; canWrite: boolean }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const { firme, ukupno: ukupnoFirmi, setUpit } = useFirme();

  const [strana, setStrana] = useState(0);
  const [redovi, setRedovi] = useState<RiskNote[] | null>(null);
  const [ukupno, setUkupno] = useState(0);
  const [ucitava, setUcitava] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const [uredjuje, setUredjuje] = useState<RiskNote | null>(null);
  const [novi, setNovi] = useState(false);

  const ucitaj = useCallback(async () => {
    setUcitava(true);
    try {
      const p = await reportsApi.riskNotes(strana);
      setRedovi(p.content);
      setUkupno(p.totalElements);
      setGreska(null);
    } catch (e) {
      setGreska((e as ApiError).message);
    } finally {
      setUcitava(false);
    }
  }, [strana]);

  useEffect(() => {
    void ucitaj();
  }, [ucitaj]);

  async function sacuvaj(body: RiskNoteRequest, id?: string) {
    if (id) await reportsApi.updateRiskNote(id, body);
    else await reportsApi.createRiskNote(body);
    setNovi(false);
    setUredjuje(null);
    await ucitaj();
    toast.success(t("report.recv.savedNote", "Beleška je upisana."));
  }

  /** Isto pravilo kao kod oznake: potvrda pre brisanja, i greška se vidi umesto da se izgubi. */
  async function obrisi(n: RiskNote) {
    const potvrda = await confirm({
      message: t("report.recv.confirmDeleteNote", {
        kupac: n.companyName ?? "—",
        defaultValue: "Obrisati belešku o riziku za {{kupac}}?",
      }),
      danger: true,
    });
    if (!potvrda) return;
    try {
      await reportsApi.deleteRiskNote(n.id);
      await ucitaj();
      toast.success(t("report.recv.deletedNote", "Beleška je obrisana."));
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  return (
    <section className="card" style={{ padding: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          padding: "14px 14px 0",
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 15 }}>{t("report.recv.riskNotes", "Rizični kupci")}</h3>
          <div className="sub">
            {t(
              "report.recv.riskNotesSub",
              "Beleška nosi svoj datum i starija se ne prepisuje: procena iz avgusta je dokaz o avgustu.",
            )}
          </div>
        </div>
        {canWrite && !novi && !uredjuje && (
          <button type="button" className="btn" onClick={() => setNovi(true)}>
            {t("report.recv.addNote", "Dodaj belešku")}
          </button>
        )}
      </div>

      {greska && <div className="card error" style={{ margin: 14 }}>{greska}</div>}

      {(novi || uredjuje) && (
        <BeleskaObrazac
          postojeci={uredjuje}
          firme={firme}
          ukupnoFirmi={ukupnoFirmi}
          onTraziFirmu={setUpit}
          onOdustani={() => {
            setNovi(false);
            setUredjuje(null);
          }}
          onSacuvaj={sacuvaj}
        />
      )}

      {redovi === null && ucitava && <TableSkeleton rows={3} />}

      {redovi !== null && redovi.length === 0 && (
        <div className="empty" style={{ padding: 14 }}>
          {t("report.recv.noNotes", "Nema upisanih beleški o rizičnim kupcima.")}
          <div className="sub" style={{ marginTop: 6 }}>
            {t(
              "report.recv.noNotesIncomplete",
              "Evidencija nije potpuna: belešku upisuje komercijala, pa prazan spisak ne znači da rizičnih kupaca nema.",
            )}
          </div>
        </div>
      )}

      {redovi !== null && redovi.length > 0 && (
        <div style={{ overflowX: "auto", opacity: ucitava ? 0.55 : 1 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("report.recv.col.customer", "Kupac")}</th>
                <th>{t("report.recv.col.notedOn", "Zabeleženo")}</th>
                <th>{t("report.recv.col.note", "Beleška")}</th>
                {canWrite && <th aria-label={t("common.actions", "Radnje")} />}
              </tr>
            </thead>
            <tbody>
              {redovi.map((n) => (
                <tr key={n.id}>
                  <td>{n.companyName ?? "—"}</td>
                  <td>{formatDate(n.notedOn, locale)}</td>
                  <td style={{ maxWidth: 520 }}>{n.note}</td>
                  {canWrite && (
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button type="button" className="btn ghost" onClick={() => setUredjuje(n)}>
                        {t("common.edit", "Uredi")}
                      </button>{" "}
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => void obrisi(n)}
                      >
                        {t("common.delete", "Obriši")}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager page={strana} size={STRANA} total={ukupno} busy={ucitava} onPage={setStrana} />
    </section>
  );
}

function BeleskaObrazac({
  postojeci,
  firme,
  ukupnoFirmi,
  onTraziFirmu,
  onOdustani,
  onSacuvaj,
}: {
  postojeci: RiskNote | null;
  firme: Company[];
  ukupnoFirmi: number;
  onTraziFirmu: (q: string) => void;
  onOdustani: () => void;
  onSacuvaj: (body: RiskNoteRequest, id?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const danas = new Date().toISOString().slice(0, 10);
  const [companyId, setCompanyId] = useState<string | null>(postojeci?.companyId ?? null);
  const [notedOn, setNotedOn] = useState(postojeci?.notedOn ?? danas);
  const [note, setNote] = useState(postojeci?.note ?? "");
  const [greska, setGreska] = useState<string | null>(null);
  const [cuva, setCuva] = useState(false);

  async function posalji(e: React.FormEvent) {
    e.preventDefault();
    if (!companyId) {
      setGreska(t("report.recv.err.company", "Izaberi kupca."));
      return;
    }
    if (!note.trim()) {
      setGreska(t("report.recv.err.note", "Beleška ne može biti prazna."));
      return;
    }
    setCuva(true);
    try {
      await onSacuvaj({ companyId, notedOn, note: note.trim() }, postojeci?.id);
    } catch (err) {
      setGreska((err as ApiError).message);
    } finally {
      setCuva(false);
    }
  }

  return (
    <form
      onSubmit={posalji}
      style={{
        margin: 14,
        padding: 14,
        border: "1px solid var(--border)",
        borderRadius: 8,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 12,
      }}
    >
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>{t("report.recv.col.customer", "Kupac")} *</span>
        <CompanyCombobox
          companies={firme}
          value={companyId}
          onChange={setCompanyId}
          onQueryChange={onTraziFirmu}
          totalMatches={ukupnoFirmi}
          placeholder={t("report.recv.pickCustomer", "Traži kupca…")}
        />
      </label>
      <label className="field">
        <span>{t("report.recv.col.notedOn", "Zabeleženo")} *</span>
        <input className="input" type="date" value={notedOn} onChange={(e) => setNotedOn(e.target.value)} />
      </label>
      <label className="field" style={{ gridColumn: "1 / -1" }}>
        <span>{t("report.recv.col.note", "Beleška")} *</span>
        <textarea className="input" rows={3} maxLength={4000} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {greska && (
        <div className="card error" style={{ gridColumn: "1 / -1" }}>
          {greska}
        </div>
      )}
      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
        <button type="submit" className="btn" disabled={cuva}>
          {t("common.save", "Sačuvaj")}
        </button>
        <button type="button" className="btn ghost" onClick={onOdustani} disabled={cuva}>
          {t("common.cancel", "Odustani")}
        </button>
      </div>
    </form>
  );
}

